import { defineStore } from 'pinia'

import Api, { parseRateLimitHeader } from '@core/api/API'

export const useAuthStore = defineStore('AuthStore', {
  state: () => ({
    redirectUrl: window.localStorage.redirectUrl || null,
    token: window.localStorage.AuthToken ? window.localStorage.AuthToken : null,
    user: null,
    temporaryLocation: null,
  }),

  getters: {
    getRedirectUrl(state) {
      return state.redirectUrl
    },
    getUser(state) {
      return state.user
    },
    isAuthenticated(state) {
      return Boolean(state.token)
    },
    getToken(state) {
      return state.token
    },
  },

  actions: {
    /**
     * Resets the store state to initial values and clears localStorage
     * @returns {void}
     */
    $reset() {
      // Clear localStorage items
      window.localStorage.removeItem('AuthToken')
      window.localStorage.removeItem('user')
      window.localStorage.removeItem('redirectUrl')

      // Reset state to initial values
      this.redirectUrl = null
      this.token = null
      this.user = null
      this.temporaryLocation = null
    },

    async authorize(loginParams) {
      const { data } = await Api.UserCalls.authorize({
        email: loginParams.email,
        password: loginParams.password,
      }, loginParams.recaptchaToken)

      this.user = data.user
      this.token = data.token
      window.localStorage.AuthToken = data.token

      return data
    },
    async refreshAuthUser() {
      if (this.token) {
        try {
          this.user = await Api.UserCalls.getAuthorizedUser(this.token)
        } catch (err) {
          if (err.response?.data?.error?.code === 2005) {
            this.user = null
            this.token = null
          } else {
            console.error(err)
          }
        }
      }
    },
    async logout() {
      const { data } = await Api.UserCalls.logout(this.token)

      window.localStorage.removeItem('AuthToken')
      window.localStorage.removeItem('user')

      this.user = null
      this.token = null

      return data
    },

    setRedirectUrl(url) {
      if (!url) {
        this.redirectUrl = null
        delete window.localStorage.redirectUrl

        return
      }

      this.redirectUrl = url
      window.localStorage.redirectUrl = url
    },

    setAuthorizedUser(user) {
      this.user = user
    },

    async register(registerParams) {
      const { data } = await Api.UserCalls.register(registerParams)

      this.user = data.user
      this.token = data.token
      window.localStorage.AuthToken = data.token

      return data
    },

    async sendVerificationEmail(verifyEmailParams) {
      const { headers } = await Api.UserCalls.sendVerificationEmail({
        email: verifyEmailParams.email,
        lang: verifyEmailParams.lang,
      }, verifyEmailParams.recaptchaToken)

      const rateLimit = parseRateLimitHeader(headers['ratelimit'])
      if (rateLimit.remaining === 0) {
        return rateLimit.reset
      }

      return 0
    },

    /**
     * @param {{
     *  email: string,
     *  recaptchaToken: string
     * }} params
     * @returns {Promise<void>}
     */
    async forgotPassword(params) {
      await Api.UserCalls.forgotPassword(params.email, params.recaptchaToken)
    },

    /**
     * @param {{
     *   code: string,
     *   password: string,
     *   recaptchaToken: string,
     * }} params
     * @returns {Promise<void>}
     */
    async resetPassword(params) {
      await Api.UserCalls.resetPassword({
        code: decodeURIComponent(params.code),
        password: params.password,
      }, params.recaptchaToken)
    },

    /**
     * @param {{
     *   oldPassword: string,
     *   newPassword: string
     * }} data
     * @returns {Promise<void>}
     */
    async changePassword(data) {
      await Api.UserCalls.changePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }, this.token)
    },

    /**
     * @param {File} avatar
     * @returns {Promise<void>}
     */
    async uploadAvatar(avatar) {
      if (this.user) {
        this.user.avatar = await Api.UserCalls.uploadAvatar(avatar, this.token)

        return this.user.avatar
      }
    },

    async deleteAvatar() {
      if (this.user) {
        await Api.UserCalls.deleteAvatar(this.token)
        this.user.avatar = null
      }
    },

    /**
     * @param {{
     *   username?: string,
     * }} data
     * @returns {Promise<typeof this.user>}
     */
    async updateProfile(data) {
      this.setAuthorizedUser(await Api.UserCalls.updateProfile({
        username: data.username,
      }, this.token))
    },

    /**
     * @param {{
     *   locationCountryId: number|null,
     *   locationStateId: number|null,
     *   locationCityId: number|null,
     * }} data
     * @returns {Promise<typeof this.user>}
     */
    async updateLocation(data) {
      this.setAuthorizedUser(await Api.UserCalls.updateLocation({
        locationCountryId: data.locationCountryId,
        locationStateId: data.locationStateId,
        locationCityId: data.locationCityId,
      }, this.token))
    },

    /**
     * @param {{
     *   locationCountryId: number|null,
     *   locationStateId: number|null,
     *   locationCityId: number|null,
     * }} data
     * @returns {void}
     */
    updateTemporaryLocation(data) {
      this.temporaryLocation = {
        locationCountryId: data.locationCountryId,
        locationStateId: data.locationStateId,
        locationCityId: data.locationCityId,
      }
    },

    /**
     * @returns {void}
     */
    async permanentlyStoreLocation() {
      if (this.temporaryLocation) {
        return this.updateLocation(this.temporaryLocation)
      }
    },

    /**
     * @param {{
     *   lang?: string
     * }} data
     * @returns {Promise<void>}
     */
    async requestDeletion(data) {
      if (this.user) {
        await Api.UserCalls.requestDeletion(data, this.token)
      }
    },

    /**
     * @param {{
     *   code: string
     * }} data
     * @returns {Promise<void>}
     */
    async confirmDeletion(data) {
      await this.refreshAuthUser()
      await Api.UserCalls.confirmDeletion(data, this.token)

      this.user = null
      this.token = null

      window.localStorage.removeItem('user')
      window.localStorage.removeItem('AuthToken')
    },
  },
})
